// src/services/auth.ts
import api, { setTokens } from "../lib/api";

export type VcgUser = {
  [x: string]: string | null | undefined | boolean | string[];
  id: string;
  userType: "member" | "expert" | "admin";
  status: "pending" | "approved" | "rejected";
  firstName: string;
  lastName: string;
  email?: string;
  photoUrl?: string | null;
  segment?: string | null;
  country?: string | null;
  selectedSectors: string[];
  verified: boolean;
  isSuperAdmin?: boolean;
  permissions?: string[];
};

type LoginResponse = {
  ok: boolean;
  accessToken: string;
  refreshToken: string;
  user: VcgUser;
};

export async function login(
  email: string,
  password: string,
  rememberMe: boolean
): Promise<{ user: VcgUser }> {
  try {
    console.log('🌐 Auth Service: About to make API call...');
    console.log('📍 API Base URL:', api.defaults.baseURL);
    console.log('📦 Request payload:', { email, password: '***', rememberMe });
    
    const response = await api.post<LoginResponse>("/api/auth/login", {
      email,
      password,
      rememberMe,
    });
    
    console.log('✅ Auth Service: API call successful!');
    console.log('📈 Response status:', response.status);
    console.log('📊 Response data:', response.data);
    
    const { data } = response;
    setTokens(data.accessToken, data.refreshToken);
    localStorage.setItem("user", JSON.stringify(data.user));
    
    return { user: data.user };
  } catch (error: any) {
    console.error('💥 Auth Service: API call failed!');
    console.error('🔍 Error object:', error);
    console.error('📡 Request config:', error.config);
    console.error('📨 Response:', error.response);
    console.error('📋 Response data:', error.response?.data);
    console.error('🔢 Response status:', error.response?.status);
    console.error('📝 Error message:', error.message);
    
    // Check if this is a network error
    if (!error.response) {
      console.error('🚫 No response received - likely network/CORS issue');
    }
    
    throw error;
  }
}

// ✅ Use userType (not memberType)
export type RegisterPayload = {
  userType: "member" | "expert";
  firstName: string;
  lastName: string;
  title?: string;
  experience?: string;
  organizationName?: string;
  organizationBrief?: string;
  segment?: string;
  country?: string;
  selectedSectors: string[];
  email: string;
  password: string;
  photoUrl?: string;
};

export async function register(payload: RegisterPayload) {
  console.log('📤 Register payload being sent:', payload);
  console.log('📍 userType value:', payload.userType);
  const { data } = await api.post("/api/auth/register", payload);
  return data;
}

export async function verifyOTP(email: string, otp: string) {
  const { data } = await api.post("/api/auth/verify-otp", { email, otp });
  return data;
}

export async function resendOTP(email: string) {
  const { data } = await api.post("/api/auth/resend-otp", { email });
  return data;
}

export async function completeRegistration(payload: RegisterPayload) {
  console.log('📤 Complete registration payload being sent:', payload);
  const { data } = await api.post("/api/auth/complete-registration", payload);
  return data;
}

export async function logout() {
  const rt = localStorage.getItem("refreshToken");
  try {
    if (rt) await api.post("/api/auth/logout", { refreshToken: rt });
  } catch {}
  setTokens(null, null);
  localStorage.removeItem("user");
}

export function getStoredUser(): VcgUser | null {
  const raw = localStorage.getItem("user");
  try {
    return raw ? (JSON.parse(raw) as VcgUser) : null;
  } catch {
    return null;
  }
}

// Invitation-based registration functions
export async function verifyInvite(token: string) {
  const { data } = await api.get(`/api/auth/verify-invite/${token}`);
  return data;
}

export type RegisterWithInvitePayload = Omit<RegisterPayload, 'email' | 'userType'> & {
  token: string;
};

export async function registerWithInvite(payload: RegisterWithInvitePayload) {
  const { data } = await api.post("/api/auth/register-with-invite", payload);

  // Auto-login with the returned tokens
  if (data.data?.accessToken && data.data?.refreshToken) {
    setTokens(data.data.accessToken, data.data.refreshToken);
    if (data.data.user) {
      localStorage.setItem("user", JSON.stringify(data.data.user));
    }
  }

  return data;
}